import EntityService, { WithSubcollection } from '@/services/EntityService'
import { Budget, BudgetItem, budgetConverter, budgetItemConverter } from '@/models/budget'
import { PurchaseOrder, purchaseOrderConverter } from '@/models/purchaseOrder'
import { WorkOrder, workOrderConverter } from '@/models/workOrder'
import ProposalService from './ProposalService'
import InvoiceService from './InvoiceService'
import BillService from './BillService'
import ProjectService from './ProjectService'
import { PROJECT_STATUS } from '@/models/project'

@WithSubcollection<BudgetItem>('lineItems', budgetItemConverter)
@WithSubcollection<PurchaseOrder>('purchaseOrders', purchaseOrderConverter)
@WithSubcollection<WorkOrder>('workOrders', workOrderConverter)
export default class BudgetService extends EntityService<Budget> {
  constructor() {
    super({ collectionName: 'budgets', converter: budgetConverter })
  }

  async find(id: string): Promise<Budget> {
    const budget = await super.find(id)
    if (!budget) {
      return null
    }

    const lineItems = await this.getLineItems(id)
    return Object.assign(budget, { lineItems })
  }

  async createBudget (budget: Budget): Promise<Budget> {
    const budgetData = JSON.parse(JSON.stringify(budget))
    delete budgetData.lineItems

    const newBudget = await this.update(budgetData.id, budgetData)
    const lineItemsPromises = budget.lineItems.map(item => this.addLineItem(newBudget.id, item))

    const lineItems = await Promise.all(lineItemsPromises)
    return Object.assign(newBudget, { lineItems })
  }

  async updateBudget (budget: Budget, deletedItems: string[]): Promise<Budget> {
    // copy budget object
    const budgetData = JSON.parse(JSON.stringify(budget))
    const itemsToDelete = deletedItems || []

    // copy line items and remove deleted items
    let lineItems = JSON.parse(JSON.stringify(budget.lineItems))
    lineItems = lineItems.filter((item: BudgetItem) => !itemsToDelete.includes(item.id))

    // remove line items field from budgetData
    delete budgetData.lineItems

    // save budget doc
    await this.update(budgetData.id, budgetData)

    const removedItemsPromises = []
    // delete removed line items
    itemsToDelete.forEach(id => {
      if (id.indexOf('generatedID') > -1) return
      removedItemsPromises.push(this.deleteLineItem(budget.id, id))
    })

    const uptodateItemsPromises = []
    // save line items
    lineItems.forEach(item => {
      if (item.id && item.id.indexOf('generatedID') === -1) {
        uptodateItemsPromises.push(this.updateLineItem(budget.id, item.id, item))
      } else {
        uptodateItemsPromises.push(this.addLineItem(budget.id, item))
      }
    })

    await Promise.all(removedItemsPromises)

    return await Promise.all(uptodateItemsPromises).then((values) => {
      return Object.assign(budgetData, { lineItems: values })
    })
  }

  async deleteBudget (budget: Budget, customerId: string, isDeletingProject: boolean = false): Promise<boolean> {
    const proposalService = new ProposalService()
    const invoiceService = new InvoiceService()
    const billService = new BillService()
    const projectService = new ProjectService()

    const allPromises = [
      proposalService.deleteProposal(budget.id),
      invoiceService.deleteBudgetInvoices(budget.id)
    ]

    const workOrders = await this.getWorkOrders(budget.id)
    workOrders.forEach((workOrder: WorkOrder) => {
      allPromises.push(this.deleteWorkOrder(budget.id, workOrder.id))
      allPromises.push(billService.deleteByWorkOrderId(workOrder.workOrderId))
    })

    const purchaseOrders = await this.getPurchaseOrders(budget.id)
    purchaseOrders.forEach((purchaseOrder: PurchaseOrder) => {
      allPromises.push(this.deletePurchaseOrder(budget.id, purchaseOrder.id))
      allPromises.push(billService.deleteByPurchaseOrderId(purchaseOrder.purchaseOrderId))
    })

    budget.lineItems.forEach((item: BudgetItem) => {
      allPromises.push(this.deleteLineItem(budget.id, item.id))
    })

    allPromises.push(this.forceDelete(budget.id))

    if (!isDeletingProject) {
      allPromises.push(projectService.updateProjectStatus(budget.id, customerId, PROJECT_STATUS.ESTIMATING))
    }

    await Promise.all(allPromises)
    return true
  }
}
